import {
  UNIT_CELL_FIELDS,
  UNIT_KIND_LABELS,
  UNIT_STATUS_LABELS,
  type UnitCellField,
} from '@sivan/shared';
import { type SyntheticEvent, useState } from 'react';
import { Modal } from './Modal';
import { FIELD_LABELS, errorText } from './labels';
import type { Cells, GridRow } from './pending';

const CHOICES: Partial<Record<UnitCellField, readonly string[]>> = {
  kind: Object.values(UNIT_KIND_LABELS),
  status: Object.values(UNIT_STATUS_LABELS),
};

interface UnitFormProps {
  readonly row: GridRow | null;
  readonly onApply: (key: string, cells: Cells) => void;
  readonly onClose: () => void;
}

/** Mobile editing: one unit at a time, into the same pending draft as the desktop grid. */
export function UnitForm({ row, onApply, onClose }: UnitFormProps) {
  const [cells, setCells] = useState<Cells | null>(row?.cells ?? null);
  if (row === null || cells === null) return null;
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    onApply(row.key, cells);
  };
  return (
    <Modal
      title={row.unitId === null ? 'יחידה חדשה' : `עריכת יחידה ${row.cells.code}`}
      open
      onClose={onClose}
    >
      <form className="unit-form" onSubmit={submit}>
        {UNIT_CELL_FIELDS.map((field) => {
          const id = `unit-${field}`;
          const error = row.errors[field];
          const options = CHOICES[field];
          return (
            <div key={field} className="field">
              <label htmlFor={id}>{FIELD_LABELS[field]}</label>
              {options === undefined ? (
                <input
                  id={id}
                  value={cells[field]}
                  inputMode={['floor', 'rooms', 'areaM2'].includes(field) ? 'decimal' : 'text'}
                  aria-invalid={error !== undefined}
                  aria-describedby={error === undefined ? undefined : `${id}-error`}
                  onChange={(event) => {
                    setCells({ ...cells, [field]: event.target.value });
                  }}
                />
              ) : (
                <select
                  id={id}
                  value={cells[field]}
                  onChange={(event) => {
                    setCells({ ...cells, [field]: event.target.value });
                  }}
                >
                  <option value="">—</option>
                  {options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              )}
              {error !== undefined && (
                <p id={`${id}-error`} className="field__error">
                  {errorText(field, error)}
                </p>
              )}
            </div>
          );
        })}
        <footer className="modal__footer">
          <button type="submit" className="button button--primary">
            עדכון הטיוטה
          </button>
          <button type="button" className="button" onClick={onClose}>
            ביטול
          </button>
        </footer>
      </form>
    </Modal>
  );
}
